import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Lock, X, Calendar } from 'lucide-react';
import './CompleteTaskActualsModal.css';

export interface CompleteTaskActualsResult {
  actualStart: Date;
  actualEnd: Date;
}

interface CompleteTaskActualsModalProps {
  taskTitle: string;
  scheduledStart: Date;
  scheduledEnd: Date;
  /** Defaults: actual start = scheduled start, actual end = today (clamped to ≥ start). */
  defaultActualStart?: Date;
  defaultActualEnd?: Date;
  onConfirm: (result: CompleteTaskActualsResult) => void;
  onCancel: () => void;
}

function toLocalIsoDate(d: Date): string {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fromLocalIsoDate(s: string): Date | null {
  const [y, m, d] = s.split('-').map((x) => parseInt(x, 10));
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d, 0, 0, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function fmtNice(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const CompleteTaskActualsModal: React.FC<CompleteTaskActualsModalProps> = ({
  taskTitle,
  scheduledStart,
  scheduledEnd,
  defaultActualStart,
  defaultActualEnd,
  onConfirm,
  onCancel,
}) => {
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const initialStart = useMemo(
    () => defaultActualStart ?? new Date(scheduledStart),
    [defaultActualStart, scheduledStart],
  );
  const initialEnd = useMemo(() => {
    const end = defaultActualEnd ?? today;
    return end < initialStart ? new Date(initialStart) : end;
  }, [defaultActualEnd, today, initialStart]);

  const [startStr, setStartStr] = useState(toLocalIsoDate(initialStart));
  const [endStr, setEndStr] = useState(toLocalIsoDate(initialEnd));

  const startDate = useMemo(() => fromLocalIsoDate(startStr), [startStr]);
  const endDate = useMemo(() => fromLocalIsoDate(endStr), [endStr]);

  const error = useMemo(() => {
    if (!startDate) return 'Pick a valid start date';
    if (!endDate) return 'Pick a valid end date';
    if (endDate < startDate) return 'End date must be on or after start date';
    return null;
  }, [startDate, endDate]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const handleConfirm = () => {
    if (error || !startDate || !endDate) return;
    onConfirm({ actualStart: startDate, actualEnd: endDate });
  };

  return (
    <div className="pm-actuals-modal-overlay" role="presentation" onClick={onCancel}>
      <div
        className="pm-actuals-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pm-actuals-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="pm-actuals-modal-header">
          <span className="pm-actuals-modal-icon" aria-hidden>
            <CheckCircle2 size={20} />
          </span>
          <div>
            <h3 id="pm-actuals-modal-title">Mark task complete</h3>
            <p className="pm-actuals-modal-task">&ldquo;{taskTitle}&rdquo;</p>
          </div>
          <button
            type="button"
            className="pm-actuals-modal-close"
            onClick={onCancel}
            aria-label="Close"
          >
            <X size={16} aria-hidden />
          </button>
        </header>

        <div className="pm-actuals-modal-body">
          <p className="pm-actuals-modal-hint">
            Capture the actual dates this task started and finished. We&rsquo;ll lock it to those
            dates so future schedule shifts won&rsquo;t move it.
          </p>

          <div className="pm-actuals-modal-grid">
            <label className="pm-actuals-modal-field">
              <span className="pm-actuals-modal-label">
                <Calendar size={13} aria-hidden /> Actual start
              </span>
              <input
                type="date"
                value={startStr}
                onChange={(e) => setStartStr(e.target.value)}
                className="pm-actuals-modal-input"
                autoFocus
              />
              <span className="pm-actuals-modal-hint-row">
                Scheduled: {fmtNice(scheduledStart)}
              </span>
            </label>
            <label className="pm-actuals-modal-field">
              <span className="pm-actuals-modal-label">
                <Calendar size={13} aria-hidden /> Actual finish
              </span>
              <input
                type="date"
                value={endStr}
                onChange={(e) => setEndStr(e.target.value)}
                className="pm-actuals-modal-input"
              />
              <span className="pm-actuals-modal-hint-row">
                Scheduled: {fmtNice(scheduledEnd)}
              </span>
            </label>
          </div>

          {error && <div className="pm-actuals-modal-error">{error}</div>}

          <div className="pm-actuals-modal-lock-note">
            <Lock size={14} aria-hidden />
            <span>
              The bar will be pinned to these dates. Unlock from the Gantt timeline to move it
              again.
            </span>
          </div>
        </div>

        <footer className="pm-actuals-modal-actions">
          <button type="button" className="pm-actuals-modal-btn pm-actuals-modal-btn--ghost" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="pm-actuals-modal-btn pm-actuals-modal-btn--primary"
            onClick={handleConfirm}
            disabled={!!error}
          >
            <CheckCircle2 size={14} aria-hidden /> Mark complete
          </button>
        </footer>
      </div>
    </div>
  );
};

export default CompleteTaskActualsModal;
